import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SocialLinks from "./SocialLinks";

/**
 * Bio card displayed at the bottom of every blog article.
 *
 * Purpose (SEO / E-E-A-T):
 *   Google's Search Quality Guidelines heavily weight "author authority" for
 *   YMYL / spiritual content. Surfacing a real author with a photo, a public
 *   bio (linked via `/histoire`) and verifiable social channels (SocialLinks)
 *   is one of the strongest E-E-A-T signals we can send from a page.
 *
 * When you upload the real portrait for Olivier, update `AUTHOR_PORTRAIT_URL`
 * below (single-line swap).
 */
const AUTHOR_PORTRAIT_URL = "/brand/olivier-portrait.jpg";

export default function AuthorCard({ className = "" }) {
    // Track image load failure so we never expose a transparent circle — if the
    // asset 404s (e.g. code deployed but static file not yet redeployed), we
    // fall back to a solid disc with the author's initials rather than let the
    // page content behind the circle bleed through.
    const [imgOk, setImgOk] = useState(true);

    return (
        <aside
            className={`mt-16 rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start ${className}`}
            data-testid="author-card"
            aria-label="À propos de l'auteur"
        >
            <div
                className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden ring-2 ring-primary/30 shrink-0 bg-primary/10 flex items-center justify-center"
                aria-hidden={!imgOk}
            >
                {imgOk ? (
                    <img
                        src={AUTHOR_PORTRAIT_URL}
                        alt="Olivier Masurel — fondateur de Foundation of Light"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={() => setImgOk(false)}
                        data-testid="author-portrait"
                    />
                ) : (
                    <span className="font-serif text-3xl text-primary" data-testid="author-portrait-fallback">
                        OM
                    </span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs tracking-[0.25em] uppercase text-primary mb-2">À propos de l'auteur</div>
                <h3 className="font-serif text-2xl mb-2" data-testid="author-name">Olivier Masurel</h3>
                <p className="text-sm text-foreground/75 leading-relaxed mb-4">
                    Fondateur de <strong>Foundation of Light</strong>, Olivier accompagne depuis plusieurs années
                    celles et ceux qui explorent le <strong>langage de lumière</strong> et les soins énergétiques.
                    Il transmet en cercle et en séance individuelle, en Présence et en Amour Universel,
                    au service de Gaïa et de toute l'humanité.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                    <Link
                        to="/histoire"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
                        data-testid="author-story-link"
                    >
                        Lire mon histoire
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <span className="text-muted-foreground/40">·</span>
                    <SocialLinks size="sm" testid="author-social" />
                </div>
            </div>
        </aside>
    );
}
